import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { ProgressBar } from './ProgressBar';
import { GlowButton } from './GlowButton';
interface QuizQuestion {
  id: number;
  question: string;
  options: string[];
}
interface QuizProps {
  onComplete: (answers: Record<number, number>) => void;
  onBack: () => void;
}
const questions: QuizQuestion[] = [{
  id: 1,
  question: '¿Cómo suelen ser sus noches juntos?',
  options: ['Siempre iguales', 'Tranquilas pero cómodas', 'Divertidas e impredecibles', 'Depende del día']
}, {
  id: 2,
  question: '¿Quién suele proponer cosas nuevas?',
  options: ['Yo', 'La otra persona', 'Ambos', 'Ninguno 😅']
}, {
  id: 3,
  question: '¿Qué no puede faltar en una buena noche?',
  options: ['Risas', 'Conversaciones profundas', 'Sorpresa', 'Tensión y misterio']
}, {
  id: 4,
  question: 'Si su conexión fuera una película, sería…',
  options: ['Romance', 'Misterio', 'Drama intenso', 'Una mezcla de todo']
}, {
  id: 5,
  question: '¿Qué tan seguido hacen algo distinto?',
  options: ['Casi nunca', 'A veces', 'Bastante seguido', 'Siempre buscamos nuevas experiencias']
}, {
  id: 6,
  question: '¿Qué tipo de experiencia los representa mejor?',
  options: ['Conexión tranquila', 'Juego y tensión', 'Desafíos inesperados', 'Explorar juntos']
}];
export function Quiz({
  onComplete,
  onBack
}: QuizProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [direction, setDirection] = useState(1);
  const question = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;
  const handleOptionSelect = (optionIndex: number) => {
    setSelectedOption(optionIndex);
  };
  const handleContinue = () => {
    if (selectedOption === null) return;
    const newAnswers = {
      ...answers,
      [question.id]: selectedOption
    };
    setAnswers(newAnswers);
    if (isLastQuestion) {
      onComplete(newAnswers);
    } else {
      setDirection(1);
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(null);
    }
  };
  const handlePrevious = () => {
    if (currentQuestion === 0) {
      onBack();
    } else {
      setDirection(-1);
      setCurrentQuestion(currentQuestion - 1);
      setSelectedOption(answers[questions[currentQuestion - 1].id] ?? null);
    }
  };
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 30 : -30,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -30 : 30,
      opacity: 0
    })
  };
  return <motion.div className="relative w-full min-h-screen flex flex-col px-6 py-8" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-46">
      {/* Header */}
      <div className="w-full max-w-md mx-auto mb-8" data-id="element-47">
        <button onClick={handlePrevious} className="mb-6 p-2 rounded-full hover:bg-white/5 transition-colors" data-id="element-48">
          <ArrowLeftIcon className="w-5 h-5 text-white/60" data-id="element-49" />
        </button>
        <ProgressBar current={currentQuestion + 1} total={questions.length} data-id="element-50" />
      </div>

      {/* Question Content */}
      <div className="flex-1 w-full max-w-md mx-auto flex flex-col justify-center" data-id="element-51">
        <AnimatePresence mode="wait" custom={direction} data-id="element-52">
          <motion.div key={currentQuestion} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="space-y-6" data-id="element-53">
            <h2 className="text-3xl md:text-4xl font-display font-bold leading-tight" data-id="element-54">
              {question.question}
            </h2>

            <div className="space-y-3 pt-4" data-id="element-55">
              {question.options.map((option, index) => <motion.button key={index} onClick={() => handleOptionSelect(index)} className={`
                    w-full px-6 py-5 rounded-2xl text-left transition-all duration-300
                    backdrop-blur-glass
                    ${selectedOption === index ? 'bg-white/8 border-2 border-accent-pink glow-pink-subtle' : 'bg-white/4 border border-white/8 hover:border-white/12 hover:bg-white/6'}
                  `} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-56">
                  <span className="text-base font-medium" data-id="element-57">{option}</span>
                </motion.button>)}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Continue Button */}
      <div className="w-full max-w-md mx-auto pt-8" data-id="element-58">
        <GlowButton onClick={handleContinue} disabled={selectedOption === null} fullWidth data-id="element-59">
          {isLastQuestion ? 'VER RESULTADO' : 'CONTINUAR →'}
        </GlowButton>
      </div>
    </motion.div>;
}